import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Minus, Plus, ShoppingBag, Sparkles, Leaf, ShieldCheck, Clock, Check, Heart } from 'lucide-react';
import { PRODUCTS } from '../mock';
import { fetchProduct, fetchProducts } from '../api';
import ProductCard from '../components/ProductCard';
import { ProductReviews } from '../components/ProductReviews';
import { useCart } from '../context/CartContext';

const ProductDetail = () => {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(() => PRODUCTS.find((p) => p.id === id) || null);
  const [loading, setLoading] = useState(!product);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [size, setSize] = useState(null);

  useEffect(() => {
    let alive = true;
    const local = PRODUCTS.find((p) => p.id === id);
    if (local) { setProduct(local); setLoading(false); }
    else {
      setLoading(true);
      fetchProduct(id).then((p) => { if (alive) { setProduct(p); setLoading(false); } })
        .catch(() => { if (alive) { setProduct(null); setLoading(false); } });
    }
    return () => { alive = false; };
  }, [id]);

  useEffect(() => {
    if (!product) return;
    setSize(product.sizes ? product.sizes[0] : null);
    const localRel = PRODUCTS.filter((p) => p.category === product.category && p.id !== product.id);
    if (localRel.length >= 4) { setRelated(localRel.slice(0, 4)); return; }
    fetchProducts(product.category).then((api) => {
      if (!Array.isArray(api)) return;
      setRelated(api.filter((p) => p.id !== product.id).slice(0, 4));
    }).catch(() => setRelated(localRel.slice(0, 4)));
  }, [product]);

  if (loading) {
    return <div className="pt-40 pb-40 text-center"><p className="font-cormorant text-2xl text-[#f5ecd9]/60 animate-pulse">Loading…</p></div>;
  }

  if (!product) {
    return (
      <div className="pt-40 pb-40 text-center">
        <p className="font-cormorant text-3xl text-[#f5ecd9]/70">Product not found.</p>
        <Link to="/shop" className="text-[#f0d582] underline mt-4 inline-block" data-testid="back-to-shop-link">Back to Shop</Link>
      </div>
    );
  }

  const price = product.price;
  const onSale = product.compareAt && product.compareAt > price;
  const comingSoon = product.status === 'coming-soon';
  const soldOut = !comingSoon && (product.in_stock === false || product.stock === 0);
  const unavailable = comingSoon || soldOut || price === null || price === undefined;
  const directions = product.directions || product.usage || product.intended_use;
  const ingredients = product.ingredients || product.materials;

  return (
    <div className="pt-28 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-5">
        <div className="text-xs font-jost text-[#f5ecd9]/40 mb-8 tracking-wider">
          <Link to="/" className="hover:text-[#ff4da6]">Home</Link> / <Link to="/shop" className="hover:text-[#ff4da6]">Shop</Link> / <span className="text-[#f0d582]">{product.name}</span>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div className="relative">
            <div className="absolute -inset-6 bg-[#d6197d]/10 blur-3xl rounded-full glow-pulse" />
            <div className="relative rounded-2xl overflow-hidden card-glass bg-[#120d10]">
              <img src={product.image} alt={product.name} className={`w-full aspect-square object-cover ${unavailable ? 'opacity-80' : ''}`} />
              {comingSoon && (<span className="absolute top-4 left-4 text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 rounded-full bg-[#0a0708]/80 border border-[#d4af37]/40 text-[#f0d582] font-jost" data-testid="coming-soon-badge">Coming Soon</span>)}
            </div>
          </div>

          <div className="flex flex-col justify-center" data-testid="product-detail">
            <div className="flex items-center gap-3 mb-3">
              {product.badge && (<span className="text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full bg-black/50 border border-[#d4af37]/40 text-[#f0d582] font-jost">{product.badge}</span>)}
            </div>
            <h1 className="font-cinzel text-4xl md:text-5xl text-gold-gradient leading-tight">{product.name}</h1>
            {product.tagline && <p className="font-cormorant italic text-xl text-[#ff4da6] mt-2">{product.tagline}</p>}
            <p className="font-jost text-[#f5ecd9]/70 leading-relaxed mt-6">{product.summary || product.description}</p>

            {Array.isArray(product.benefits) && product.benefits.length > 0 && (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="product-benefits">
                {product.benefits.map((b) => (
                  <div key={b} className="flex items-center gap-2.5 text-sm font-jost text-[#f5ecd9]/80">
                    <Check className="h-4 w-4 text-[#ff4da6] shrink-0" /> {b}
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-4 mt-6">
              <div className="flex items-center gap-2 text-sm text-[#f5ecd9]/60 font-jost"><Leaf className="h-4 w-4 text-[#ff4da6]" /> Pure botanicals</div>
              <div className="flex items-center gap-2 text-sm text-[#f5ecd9]/60 font-jost"><Sparkles className="h-4 w-4 text-[#f0d582]" /> Handcrafted</div>
              {comingSoon ? (
                <div className="flex items-center gap-2 text-sm text-[#f0d582] font-jost"><Clock className="h-4 w-4" /> Coming soon</div>
              ) : soldOut ? (
                <div className="flex items-center gap-2 text-sm text-[#f5ecd9]/60 font-jost"><ShieldCheck className="h-4 w-4 text-[#ff4da6]" /> Currently out of stock</div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-[#f5ecd9]/60 font-jost"><ShieldCheck className="h-4 w-4 text-[#ff4da6]" /> In stock</div>
              )}
            </div>

            <div className="flex items-baseline gap-3 mt-8">
              {price !== null && price !== undefined ? (
                <span className="font-cinzel text-3xl text-gold-gradient" data-testid="product-price">${price.toFixed(2)}</span>
              ) : (
                <span className="font-cinzel text-2xl text-[#f0d582]">Coming Soon</span>
              )}
              {onSale && (<span className="text-lg text-[#f5ecd9]/40 line-through">${product.compareAt.toFixed(2)}</span>)}
            </div>

            {product.sizes && (
              <div className="mt-6">
                <p className="font-jost text-xs uppercase tracking-widest text-[#f5ecd9]/60 mb-2">Size</p>
                <div className="flex gap-3">
                  {product.sizes.map((s) => (
                    <button key={s} onClick={() => setSize(s)} className={`px-5 py-2 rounded-full text-sm font-jost transition-all ${size === s ? 'bg-gradient-to-r from-[#d6197d] to-[#ff4da6] text-white' : 'border border-[#d4af37]/30 text-[#f5ecd9]/70 hover:border-[#ff4da6]/60'}`}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-5 mt-6">
              <div className="flex items-center gap-4 border border-[#d4af37]/30 rounded-full px-4 py-2">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="text-[#f0d582]"><Minus className="h-4 w-4" /></button>
                <span className="font-jost w-6 text-center">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="text-[#f0d582]"><Plus className="h-4 w-4" /></button>
              </div>
              <button
                onClick={() => !unavailable && addItem({ ...product, name: size ? `${product.name} (${size})` : product.name }, qty)}
                disabled={unavailable}
                data-testid="add-to-cart-btn"
                className={`flex-1 flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold tracking-wide ${unavailable ? 'bg-white/10 text-[#f5ecd9]/40 cursor-not-allowed' : 'btn-gold text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37]'}`}>
                {comingSoon ? 'Coming Soon' : soldOut ? 'Out of Stock' : (<><ShoppingBag className="h-4 w-4" /> Add to Cart</>)}
              </button>
            </div>
            {!unavailable && <p className="text-xs text-[#f5ecd9]/40 mt-4 font-jost">New customers: 20% off your first order of $20+ with an account · Shipping calculated at checkout</p>}

            {(ingredients || directions || product.safety_info || product.weight_size || product.size) && (
              <div className="mt-8 space-y-4 border-t border-[#d4af37]/15 pt-6">
                {(product.weight_size || product.size) && (<div><p className="font-jost text-[11px] uppercase tracking-widest text-[#f0d582] mb-1">Size</p><p className="font-jost text-sm text-[#f5ecd9]/70">{product.weight_size || product.size}</p></div>)}
                {ingredients && (<div><p className="font-jost text-[11px] uppercase tracking-widest text-[#f0d582] mb-1">Ingredients / Materials</p><p className="font-jost text-sm text-[#f5ecd9]/70">{ingredients}</p></div>)}
                {directions && (<div><p className="font-jost text-[11px] uppercase tracking-widest text-[#f0d582] mb-1">How to Use</p><p className="font-jost text-sm text-[#f5ecd9]/70">{directions}</p></div>)}
                {product.safety_info && (<div><p className="font-jost text-[11px] uppercase tracking-widest text-[#ff4da6] mb-1">Safety &amp; Care</p><p className="font-jost text-xs text-[#f5ecd9]/55 leading-relaxed">{product.safety_info}</p></div>)}
              </div>
            )}
          </div>
        </div>

        {product.founder_note && (
          <div className="mt-20 max-w-3xl mx-auto text-center" data-testid="founder-note">
            <div className="inline-flex items-center gap-2 text-[#ff4da6] mb-3"><Heart className="h-4 w-4" /><span className="font-jost text-xs tracking-[0.3em] uppercase">From the Founder</span></div>
            <p className="font-cormorant italic text-2xl md:text-3xl text-[#f5ecd9]/85 leading-relaxed">"{product.founder_note}"</p>
            <p className="font-jost text-xs tracking-[0.25em] uppercase text-[#f0d582] mt-5">Prestige Elements · Since 2019</p>
          </div>
        )}

        <div className="mt-24">
          <h2 className="font-cinzel text-3xl text-gold-gradient text-center mb-2">Reviews</h2>
          <div className="gold-divider mb-10" />
          <ProductReviews productId={product.id} showForm={true} showHistorical={true} />
        </div>

        {related.length > 0 && (
          <div className="mt-24">
            <h2 className="font-cinzel text-3xl text-gold-gradient text-center mb-2">You May Also Love</h2>
            <div className="gold-divider mb-10" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-7">
              {related.map((p) => (<ProductCard key={p.id} product={p} />))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
